import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { BACKEND_URL } from '../config/api';
import './InternshipApplications.css';

// ─── Toast helper ────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div className={`intern-toast ${type}`}>{message}</div>;
}

// ─── Status badge helper ─────────────────────────────────────────
function StatusBadge({ status }) {
  const statusMap = {
    PENDING: { label: 'Pending', cls: 'status-pending' },
    REVIEWED: { label: 'Reviewed', cls: 'status-reviewed' },
    ACCEPTED: { label: 'Accepted', cls: 'status-accepted' },
    REJECTED: { label: 'Rejected', cls: 'status-rejected' },
  };
  const { label, cls } = statusMap[status] || { label: status || 'Unknown', cls: 'status-pending' };
  return <span className={`intern-status-badge ${cls}`}>{label}</span>;
}

// ─── Main Component ──────────────────────────────────────────────
export default function InternshipApplications() {
  const navigate = useNavigate();

  // Auth
  const [authChecked, setAuthChecked] = useState(false);

  // Applications list
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [pageSizeInput, setPageSizeInput] = useState('20');
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLast, setIsLast] = useState(false);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [technologyFilter, setTechnologyFilter] = useState('ALL');

  // Detail modal
  const [selectedApp, setSelectedApp] = useState(null);

  // Resume download state
  const [downloadingId, setDownloadingId] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // ─── Auth check ────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated()) {
      const tm = setTimeout(() => {
        if (!isAuthenticated()) navigate('/signup');
        else setAuthChecked(true);
      }, 100);
      return () => clearTimeout(tm);
    }
    setAuthChecked(true);
  }, [navigate]);

  // ─── Fetch applications (paginated) ───────────────────────────
  const fetchApplications = useCallback(async (pg = 0) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${BACKEND_URL}/api/admin/internship/applications?page=${pg}&size=${size}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (res.status === 403) throw new Error('No access – Admin privileges required');
      if (!res.ok) throw new Error(`API returned status ${res.status}`);
      const data = await res.json();

      if (data.content && Array.isArray(data.content)) {
        setApplications(data.content);
        setTotalElements(data.totalElements ?? 0);
        setTotalPages(data.totalPages ?? 1);
        setIsLast(data.last ?? false);
      } else if (Array.isArray(data)) {
        setApplications(data);
        setTotalElements(data.length);
        setTotalPages(1);
        setIsLast(true);
      } else {
        setApplications([]);
        setTotalElements(0);
      }
    } catch (err) {
      console.error('Error fetching internship applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [size]);

  useEffect(() => {
    if (authChecked) fetchApplications(page);
  }, [authChecked, fetchApplications, page]);

  // ─── Resume download ──────────────────────────────────────────
  const handleDownloadResume = async (appId, applicantName) => {
    setDownloadingId(appId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/internship/applications/${appId}/resume`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders({ Accept: 'application/pdf,application/octet-stream' }),
      });
      if (!res.ok) throw new Error(`Failed to download resume: ${res.status}`);

      const blob = await res.blob();
      const contentDisposition = res.headers.get('Content-Disposition');
      let filename = `${applicantName || 'applicant'}_resume.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast(`Downloaded resume for ${applicantName || 'applicant'}`);
    } catch (err) {
      console.error('Error downloading resume:', err);
      showToast('Failed to download resume: ' + err.message, 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  // ─── Client-side filtering ────────────────────────────────────
  const filteredApps = applications.filter((app) => {
    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const match =
        (app.name || '').toLowerCase().includes(q) ||
        (app.email || '').toLowerCase().includes(q) ||
        (app.technology || '').toLowerCase().includes(q) ||
        (app.college || '').toLowerCase().includes(q) ||
        (app.phone || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    // Status filter
    if (statusFilter !== 'ALL' && app.status !== statusFilter) return false;
    // Technology filter
    if (technologyFilter !== 'ALL' && app.technology !== technologyFilter) return false;
    return true;
  });

  // Unique technologies for filter dropdown
  const technologies = [...new Set(applications.map((a) => a.technology).filter(Boolean))].sort();

  // ─── Stats ─────────────────────────────────────────────────────
  const statCounts = {
    total: totalElements,
    pending: applications.filter((a) => a.status === 'PENDING').length,
    reviewed: applications.filter((a) => a.status === 'REVIEWED').length,
    accepted: applications.filter((a) => a.status === 'ACCEPTED').length,
    rejected: applications.filter((a) => a.status === 'REJECTED').length,
  };

  // ─── Format date ──────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // ─── Loading / Error states ────────────────────────────────────
  if (!authChecked || loading) {
    return (
      <div className="intern-page">
        <div className="intern-container">
          <div className="intern-loading">Loading internship applications...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="intern-page">
        <div className="intern-container">
          <div className="intern-error">
            <h2>Error</h2>
            <p>{error}</p>
            <button className="intern-btn-retry" onClick={() => fetchApplications(page)}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="intern-page">
      <div className="intern-container">
        {/* Header */}
        <div className="intern-header">
          <h1>🎓 Internship Applications</h1>
          <p className="intern-admin-note">Admin View – Read Only</p>
        </div>

        {/* Stats row */}
        <div className="intern-stats">
          <div className="intern-stat-card">
            <span className="intern-stat-value">{statCounts.total}</span>
            <span className="intern-stat-label">Total</span>
          </div>
          <div className="intern-stat-card stat-pending">
            <span className="intern-stat-value">{statCounts.pending}</span>
            <span className="intern-stat-label">Pending</span>
          </div>
          <div className="intern-stat-card stat-reviewed">
            <span className="intern-stat-value">{statCounts.reviewed}</span>
            <span className="intern-stat-label">Reviewed</span>
          </div>
          <div className="intern-stat-card stat-accepted">
            <span className="intern-stat-value">{statCounts.accepted}</span>
            <span className="intern-stat-label">Accepted</span>
          </div>
          <div className="intern-stat-card stat-rejected">
            <span className="intern-stat-value">{statCounts.rejected}</span>
            <span className="intern-stat-label">Rejected</span>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="intern-toolbar">
          <div className="intern-search-bar">
            <span className="intern-search-icon">🔍</span>
            <input
              type="text"
              className="intern-search-input"
              placeholder="Search by name, email, technology, college, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="intern-search-clear" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>

          <div className="intern-filters">
            <select
              className="intern-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="REVIEWED">Reviewed</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <select
              className="intern-filter-select"
              value={technologyFilter}
              onChange={(e) => setTechnologyFilter(e.target.value)}
            >
              <option value="ALL">All Technologies</option>
              {technologies.map((tech) => (
                <option key={tech} value={tech}>
                  {tech}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results info */}
        <div className="intern-results-info">
          Showing {filteredApps.length} of {applications.length} on this page
          {totalElements > 0 && ` (${totalElements} total)`}
        </div>

        {/* Table */}
        {filteredApps.length > 0 ? (
          <div className="intern-table-wrapper">
            <table className="intern-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Technology</th>
                  <th>College</th>
                  <th>Qualification</th>
                  <th>Graduation</th>
                  <th>Experience</th>
                  <th>Status</th>
                  <th>Applied On</th>
                  <th>Resume</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((app, index) => (
                  <tr key={app.id || index}>
                    <td className="intern-sl">{page * size + index + 1}</td>
                    <td className="intern-name">{app.name || 'N/A'}</td>
                    <td className="intern-email">{app.email || 'N/A'}</td>
                    <td>{app.phone || 'N/A'}</td>
                    <td>
                      <span className="intern-tech-badge">{app.technology || 'N/A'}</span>
                    </td>
                    <td>{app.college || 'N/A'}</td>
                    <td>{app.qualification || 'N/A'}</td>
                    <td>{app.graduationYear || 'N/A'}</td>
                    <td>{app.experience || 'N/A'}</td>
                    <td>
                      <StatusBadge status={app.status} />
                    </td>
                    <td className="intern-date">{formatDate(app.createdAt)}</td>
                    <td>
                      <button
                        className="intern-btn-download"
                        onClick={() => handleDownloadResume(app.id, app.name)}
                        disabled={(!app.hasResume && !app.resumeFileName) || downloadingId === app.id}
                        title={app.hasResume || app.resumeFileName ? 'Download Resume' : 'No resume uploaded'}
                      >
                        {downloadingId === app.id ? '⏳' : '⬇️'}{' '}
                        {downloadingId === app.id ? 'Downloading...' : 'Resume'}
                      </button>
                    </td>
                    <td>
                      <button className="intern-btn-view" onClick={() => setSelectedApp(app)}>
                        👁️ View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="intern-no-data">
            {searchQuery || statusFilter !== 'ALL' || technologyFilter !== 'ALL'
              ? 'No applications match the current filters'
              : 'No internship applications found'}
          </div>
        )}

        {/* Pagination */}
        <div className="intern-pagination">
          <button
            className="intern-btn-page"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            ← Previous
          </button>

          <div className="intern-page-size-control">
            <label htmlFor="internPageSize">Page Size:</label>
            <input
              id="internPageSize"
              type="number"
              className="intern-page-size-input"
              value={pageSizeInput}
              min={1}
              max={500}
              onChange={(e) => setPageSizeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = parseInt(pageSizeInput, 10);
                  if (val > 0 && val <= 500) {
                    setSize(val);
                    setPage(0);
                  } else {
                    setPageSizeInput(String(size));
                  }
                }
              }}
              onBlur={() => {
                const val = parseInt(pageSizeInput, 10);
                if (!val || val < 1 || val > 500) setPageSizeInput(String(size));
              }}
            />
            <span className="intern-page-hint">Press Enter</span>
          </div>

          <span className="intern-page-info">
            Page {page + 1} of {totalPages}
            {totalElements > 0 && ` • ${totalElements} total`}
          </span>

          <button
            className="intern-btn-page"
            onClick={() => setPage((p) => p + 1)}
            disabled={loading || isLast || (page + 1) >= totalPages}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedApp && (
        <div className="intern-modal-overlay" onClick={(e) => e.target === e.currentTarget && setSelectedApp(null)}>
          <div className="intern-modal">
            <button className="intern-modal-close" onClick={() => setSelectedApp(null)}>
              ✕
            </button>
            <h2 className="intern-modal-title">📋 Application Details</h2>

            <div className="intern-detail-grid">
              <div className="intern-detail-item">
                <span className="intern-detail-label">Name</span>
                <span className="intern-detail-value">{selectedApp.name || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Email</span>
                <span className="intern-detail-value">{selectedApp.email || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Phone</span>
                <span className="intern-detail-value">{selectedApp.phone || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Technology</span>
                <span className="intern-detail-value">
                  <span className="intern-tech-badge">{selectedApp.technology || 'N/A'}</span>
                </span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">College</span>
                <span className="intern-detail-value">{selectedApp.college || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Qualification</span>
                <span className="intern-detail-value">{selectedApp.qualification || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Graduation Year</span>
                <span className="intern-detail-value">{selectedApp.graduationYear || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Experience</span>
                <span className="intern-detail-value">{selectedApp.experience || 'N/A'}</span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Status</span>
                <span className="intern-detail-value">
                  <StatusBadge status={selectedApp.status} />
                </span>
              </div>
              <div className="intern-detail-item">
                <span className="intern-detail-label">Applied On</span>
                <span className="intern-detail-value">{formatDate(selectedApp.createdAt)}</span>
              </div>
              {selectedApp.linkedinUrl && (
                <div className="intern-detail-item">
                  <span className="intern-detail-label">LinkedIn</span>
                  <span className="intern-detail-value">
                    <a href={selectedApp.linkedinUrl} target="_blank" rel="noopener noreferrer" className="intern-link">
                      {selectedApp.linkedinUrl}
                    </a>
                  </span>
                </div>
              )}
              {selectedApp.portfolioUrl && (
                <div className="intern-detail-item">
                  <span className="intern-detail-label">Portfolio</span>
                  <span className="intern-detail-value">
                    <a href={selectedApp.portfolioUrl} target="_blank" rel="noopener noreferrer" className="intern-link">
                      {selectedApp.portfolioUrl}
                    </a>
                  </span>
                </div>
              )}
              {selectedApp.resumeFileName && (
                <div className="intern-detail-item">
                  <span className="intern-detail-label">Resume File</span>
                  <span className="intern-detail-value">{selectedApp.resumeFileName}</span>
                </div>
              )}
            </div>

            {selectedApp.description && (
              <div className="intern-detail-description">
                <span className="intern-detail-label">Description / Cover Note</span>
                <p className="intern-detail-desc-text">{selectedApp.description}</p>
              </div>
            )}

            <div className="intern-modal-actions">
              <button
                className="intern-btn-download"
                onClick={() => handleDownloadResume(selectedApp.id, selectedApp.name)}
                disabled={(!selectedApp.hasResume && !selectedApp.resumeFileName) || downloadingId === selectedApp.id}
              >
                {downloadingId === selectedApp.id ? '⏳ Downloading...' : '⬇️ Download Resume'}
              </button>
              <button className="intern-btn-close" onClick={() => setSelectedApp(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
